// Copyright (c) StrangeBee 2023

import React, { ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocalMessage } from '@Helpers/locale';
import client from '@Client';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import useUserPermissions from '@Hooks/useUserPermissions';
import { THLayout } from '@Molecules/THLayout';
import { THList } from '@Molecules/THList';
import { useGenericQuery } from '@Queries/genericQueries';
import useListQuery from '@Hooks/useListQuery';
import { columnsRenderer } from '@Hooks/useConfigList/helper/computeHeader';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { ColumnTitle } from '@Atoms/ColumnTitle/ColumnTitle';
import { ICONS } from '@Constants/iconsApp';
import {
    LdapServersType,
    LDAPServerType,
    AdminPermissionsEnum,
    PermissionsEnum,
    ProfileOutput,
    OrganisationOutput,
} from 'thehive-sdk';
import { LdapServerManage } from '@Organisms/Ldap/LdapServerManage/LdapServerManage';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import { UnavailableMessage } from '@Molecules/UnavailableMessage/UnavailableMessage';
import { StatusContext } from '@Context/StatusProvider';
import usePageTitle from '@Hooks/usePageTitle';

import './LdapServers.scss';

const LdapServers = (): ReactElement => {
    const i = useLocalMessage();
    const [organisationOrdered, setOrganisationOrdered] = useState<OrganisationOutput[]>([]);

    const { licenseCapabilities } = useContext(StatusContext);

    const userSyncCapability = useMemo<boolean>(
        () => Object.keys(licenseCapabilities.userSync || {}).includes('userSync'),
        [licenseCapabilities],
    );

    const {
        data: configLdap,
        refetch: listRefetch,
        status,
    } = useGenericQuery<LdapServersType>({
        fetchCallback: () => client.ConfigController.getLdapServers(),
        key: ['list-all-ldap-servers'],
    });

    const { data: profileList = [] } = useListQuery<ProfileOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'listProfile' }],
                name: 'get-all-profiles',
                sort: ['+name'],
            },
        },
        controller: client.ProfileController,
        manual: true,
        fetchOnStart: true,
    });

    const { data: organisationList = [] } = useListQuery<OrganisationOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'listOrganisation' }],
                name: 'get-all-organisations',
                sort: ['+locked', '+name'],
            },
        },
        controller: client.OrganisationController,
        manual: true,
        fetchOnStart: true,
    });

    const serverList = useMemo(() => configLdap?.value || [], [configLdap]);
    const namesList: string[] = useMemo(() => serverList.map(({ name }) => name), [serverList]);

    useEffect(() => {
        if (organisationList) {
            const organisationListTmp = organisationList.sort(({ name: nameA }, { name: nameB }) =>
                nameA.localeCompare(nameB),
            );
            setOrganisationOrdered(organisationListTmp);
        }
    }, [organisationList]);

    const deleteServerLdap = useCallback(
        (name: string): Promise<any> => {
            const cleanServer = serverList.filter((server: LDAPServerType) => server.name !== name);
            return client.ConfigController.editLdapServers({ value: cleanServer });
        },
        [serverList],
    );

    const editServerLdap = useCallback(
        (name: string, data: LDAPServerType): Promise<any> => {
            const cleanServer = serverList.map((server: LDAPServerType) => (server.name !== name ? server : data));
            return client.ConfigController.editLdapServers({ value: cleanServer });
        },
        [serverList],
    );

    const addServerLdap = useCallback(
        (data: LDAPServerType): Promise<any> => {
            return client.ConfigController.editLdapServers({ value: [...serverList, data] });
        },
        [serverList],
    );

    const hasPermissions = useUserPermissions({ permissionsNeeded: [AdminPermissionsEnum.MANAGE_CONFIG] });

    const columns = columnsRenderer<LDAPServerType>([
        {
            title: <ColumnTitle>{i('configPlateform.ldap.config-name')}</ColumnTitle>,
            className: 'custom-table__cell',
            render: (server): ReactElement => (
                <LdapServerManage
                    data={server}
                    namesList={namesList}
                    profileList={profileList}
                    organisationList={organisationOrdered}
                    onFinish={(server) =>
                        editServerLdap(server.name, server)
                            .then(() => {
                                listRefetch();
                                openMessage('success', i('configPlateform.ldap.message.updated'));
                            })
                            .catch(handleNotificationError)
                    }
                    triggerElem={<THButton type="link">{server.name}</THButton>}
                />
            ),
        },
        {
            className: 'ldap-list__field-action custom-table__cell',
            render: (server): ReactElement => (
                <ActionsList
                    actionList={{
                        delete: {
                            label: i('general.delete'),
                            icon: <ICONS.DeleteIcon />,
                            query: () => {
                                deleteServerLdap(server.name)
                                    .then(() => {
                                        listRefetch();
                                        openMessage('success', i('configPlateform.ldap.message.deleted'));
                                    })
                                    .catch(handleNotificationError);
                            },
                        },
                    }}
                    scope="single"
                    isIcon
                />
            ),
        },
    ]);

    usePageTitle(i('page-title.admin.platform.ldap-servers'));

    return (
        <THLayout>
            {userSyncCapability ? (
                <THList>
                    <THList.Header>
                        <THList.Slot name="MANAGE">
                            {hasPermissions && (
                                <LdapServerManage
                                    namesList={namesList}
                                    profileList={profileList}
                                    organisationList={organisationOrdered}
                                    onFinish={(server) =>
                                        addServerLdap(server)
                                            .then(() => {
                                                listRefetch();
                                                openMessage('success', i('configPlateform.ldap.message.added'));
                                            })
                                            .catch(handleNotificationError)
                                    }
                                    triggerElem={<AddButton label={i('configPlateform.ldap.cta.add')} />}
                                />
                            )}
                        </THList.Slot>
                    </THList.Header>

                    <THList.Content
                        dataSource={serverList.sort(({ name: nameA }, { name: nameB }) => nameA.localeCompare(nameB))}
                        columns={columns}
                        rowKey="name"
                        emptyPlaceholder={
                            <DisplayEmptyData
                                spin={status === 'loading'}
                                label={i('configPlateform.ldap.message.no-config')}
                            >
                                {hasPermissions && (
                                    <LdapServerManage
                                        namesList={namesList}
                                        profileList={profileList}
                                        organisationList={organisationOrdered}
                                        onFinish={(server) =>
                                            addServerLdap(server)
                                                .then(() => {
                                                    listRefetch();
                                                    openMessage('success', i('configPlateform.ldap.message.added'));
                                                })
                                                .catch(handleNotificationError)
                                        }
                                        triggerElem={
                                            <THButton type="link">{i('configPlateform.ldap.cta.add')}</THButton>
                                        }
                                    />
                                )}
                            </DisplayEmptyData>
                        }
                    />
                </THList>
            ) : (
                <UnavailableMessage type="capability" />
            )}
        </THLayout>
    );
};

export const Route: RouteType = {
    menu: {
        label: 'mainLayout.adminMenu.ldap-servers',
        icon: <ICONS.ServerOutlined />,
    },

    path: RouteBuilder.compute('administration.ldap-servers'),
    permissionNeeded: [PermissionsEnum.MANAGE_CONFIG],
    content: <LdapServers />,
    shared: true,
    space: ['admin'],

    breadcrumb: { label: 'breadcrumb.global.ldap-servers' },
};
